import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { BlogRenderer } from "@/components/blog/BlogRenderer";
import { Img } from "@/components/ui/img";
import { CheckCircle, XCircle, User, CalendarDots, FileText } from "@phosphor-icons/react";
import { PostStatusBadge } from "./PostStatusBadge";
import { formatBlogDate } from "./utils";
import type { BlogPost } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { blogPostByIdQueryOptions } from "@/api-queries/blog";

export function BlogPreviewDialog({
  post,
  onClose,
  onReject,
  onApprove,
}: {
  post: BlogPost;
  onClose: () => void;
  onReject: (id: string) => void;
  onApprove: (id: string) => void;
}) {
  // Lista ne nosi tijelo članka; pregled ga učitava po id-u.
  const { data: full, isLoading } = useQuery({
    ...blogPostByIdQueryOptions(post.id),
    enabled: !post.content,
  });
  const content = post.content ?? (full as BlogPost | undefined)?.content;

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <div className="flex flex-col gap-2">
          <AdminDialogHeading icon={FileText} title={post.title} />
          <div className="flex flex-wrap items-center gap-3 pl-[52px] text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <User className="size-3.5" />
              {post.authorName || "Nepoznat"}
            </span>
            <span className="inline-flex items-center gap-1">
              <CalendarDots className="size-3.5" />
              {formatBlogDate(post.publishedAt || post.createdAt)}
            </span>
            <PostStatusBadge status={post.status} />
          </div>
        </div>
      </DialogHeader>
      <DialogBody className="flex flex-col gap-5">
        {post.coverImageUrl && (
          <Img
            src={post.coverImageUrl}
            alt=""
            className="h-48 w-full rounded-xl object-cover"
          />
        )}
        <div className="ProseMirror">
          {content ? (
            <BlogRenderer content={content} />
          ) : (
            <p className="text-sm text-muted-foreground">
              {isLoading ? "Učitavanje..." : "Sadržaj nije dostupan."}
            </p>
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Zatvori
        </Button>
        {post.status === "pending" && (
          <>
            <Button
              variant="destructive"
              onClick={() => {
                onClose();
                onReject(post.id);
              }}
            >
              <XCircle className="size-4" />
              Odbij
            </Button>
            <Button
              onClick={() => {
                onApprove(post.id);
                onClose();
              }}
            >
              <CheckCircle className="size-4" />
              Odobri
            </Button>
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
}
